import createService from '../factory';
import ApiService from '../api.service';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import { FirestoreCollectionIDs } from 'core/constants/firestore-collection-ids';
import { IEligibleDriver, ITransportGroup, ITransportGroupPreview } from './transport-groups-api-interface';

/**
 * Transport runs.
 *
 * Reading them is a Firestore query; changing one is not — the same split as the
 * request queue. The screen subscribes to the collection directly, so a run a
 * driver accepts on the road changes in the office without anybody refreshing,
 * and the callables are kept for the writes, where the rules about what may
 * happen when live on the server and claiming has to be a transaction.
 *
 * The write helpers that come with the collection service are unusable here by
 * design: `firestore.rules` refuses every direct write to this collection.
 */
const TransportGroupsApiService = {
  ...createService<ITransportGroup, ITransportGroup>(FirestoreCollectionIDs.TRANSPORT_GROUPS),

  /**
   * What a run of these requests would look like. Writes nothing — the office
   * is deciding while looking at it, and a preview that had created jobs would
   * leave them held with no run the moment the dialog closed.
   */
  preview: (requestUids: string[]): Promise<{ preview: ITransportGroupPreview }> =>
    ApiService.callFunction<{ preview: ITransportGroupPreview }>(
      { requestUids },
      FirebaseFunctionNames.PREVIEW_TRANSPORT_GROUP,
    ),

  /** Transport drivers, plus anyone currently out on a logistics run. */
  drivers: (): Promise<{ drivers: IEligibleDriver[] }> =>
    ApiService.callFunction<{ drivers: IEligibleDriver[] }>({}, FirebaseFunctionNames.GET_TRANSPORT_RUN_DRIVERS),

  assign: (uid: string, driver: { uid: string; fullName: string }): Promise<{ group: ITransportGroup }> =>
    ApiService.callFunction<{ group: ITransportGroup }>(
      { uid, driver },
      FirebaseFunctionNames.ASSIGN_TRANSPORT_GROUP,
    ),

  post: (uid: string): Promise<{ group: ITransportGroup }> =>
    ApiService.callFunction<{ group: ITransportGroup }>({ uid }, FirebaseFunctionNames.POST_TRANSPORT_GROUP),

  cancel: (uid: string): Promise<{ group: ITransportGroup }> =>
    ApiService.callFunction<{ group: ITransportGroup }>({ uid }, FirebaseFunctionNames.CANCEL_TRANSPORT_GROUP),
};

export default TransportGroupsApiService;
